import { Link } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import type { Share } from '../types'
import { cn } from '@/lib/utils'
import { TableRow, TableCell } from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'

function relativeTime(dateStr: string): string {
  const diff = new Date(dateStr).getTime() - Date.now()
  const absDiff = Math.abs(diff)
  const past = diff < 0

  if (absDiff < 60_000) return past ? 'just now' : 'in a moment'
  if (absDiff < 3_600_000) {
    const m = Math.floor(absDiff / 60_000)
    return past ? `${m}m ago` : `in ${m}m`
  }
  if (absDiff < 86_400_000) {
    const h = Math.floor(absDiff / 3_600_000)
    return past ? `${h}h ago` : `in ${h}h`
  }
  const d = Math.floor(absDiff / 86_400_000)
  return past ? `${d}d ago` : `in ${d}d`
}

export function ShareCard({
  share,
  onStatusToggle,
  onDelete,
  selected,
  onToggleSelect,
}: {
  share: Share
  onStatusToggle?: (share: Share) => void
  onDelete?: (share: Share) => void
  selected?: boolean
  onToggleSelect?: (id: number) => void
}) {
  const isDraft = share.status === 'draft'
  const isPublished = share.status === 'published' && !share.expired

  return (
    <TableRow className={'cursor-pointer ' + (selected ? 'bg-muted/30' : '')}>
      {onToggleSelect && (
        <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(share.id)}
            className="h-4 w-4"
          />
        </TableCell>
      )}
      <TableCell className="text-sm font-mono text-muted-foreground">
        <Link to={`/shares/${share.id}`} className="block">{share.id}</Link>
      </TableCell>
      <TableCell className="text-sm font-medium">
        <Link to={`/shares/${share.id}`} className="text-foreground hover:text-seed-green-600">
          {share.name || <span className="text-muted-foreground italic">unnamed</span>}
        </Link>
      </TableCell>
      <TableCell className="text-sm">
        <span className="text-foreground font-medium">{share.seed}</span>
        {share.version && (
          <span className="ml-1.5 text-xs font-mono text-muted-foreground">v{share.version}</span>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground max-w-xs whitespace-pre-wrap break-words">
        {share.note || <span className="text-muted-foreground/50">—</span>}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground" title={share.workspaces.map(w => w.name).join(', ')}>
        {share.workspaces.length || <span className="text-muted-foreground/50">0</span>}
        {share.workspaces.length > 0 && (
          <span className="ml-1 text-xs text-muted-foreground/70">
            {' · '}
            {share.workspaces.reduce((n, w) => n + w.members.length, 0)} member{share.workspaces.reduce((n, w) => n + w.members.length, 0) === 1 ? '' : 's'}
          </span>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground" title={share.expiresAt}>
        {relativeTime(share.expiresAt)}
      </TableCell>
      <TableCell className="text-sm">
        {share.expired ? (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-gray-100 text-gray-700">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-500" />
            Expired
          </span>
        ) : (
          <label
            className="inline-flex items-center gap-2 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <Switch
              checked={isPublished}
              onCheckedChange={() => onStatusToggle?.(share)}
              className="scale-90"
            />
            <span className={cn('text-xs font-medium', isPublished ? 'text-emerald-700' : 'text-muted-foreground')}>
              {isPublished ? 'Published' : 'Draft'}
            </span>
          </label>
        )}
      </TableCell>
      <TableCell className="text-right">
        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-destructive hover:text-destructive"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(share) }}
            title="Delete share"
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  )
}
